import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Search, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import productApi from '@/lib/productApi';
import type {
  HubSpotUserOption,
  ProductReviewGroupRef,
  ProductReviewer,
  ReviewerGroup,
} from '@/types/product';

interface ReviewableProduct {
  id: string;
  reviewers: ProductReviewer[];
  reviewGroups: ProductReviewGroupRef[];
}

interface ProductReviewersDialogProps {
  open: boolean;
  products: ReviewableProduct[];
  onOpenChange: (open: boolean) => void;
  onSaved: (result: {
    reviewers: ProductReviewer[];
    groups: Array<{ id: string; name: string }>;
  }) => void;
  onError: (message: string, error: unknown) => void;
  /** Opent het beheerscherm voor keuzegroepen. */
  onManageGroups: () => void;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

export default function ProductReviewersDialog({
  open,
  products,
  onOpenChange,
  onSaved,
  onError,
  onManageGroups,
}: ProductReviewersDialogProps) {
  const [users, setUsers] = useState<HubSpotUserOption[]>([]);
  const [groups, setGroups] = useState<ReviewerGroup[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [nextUsers, nextGroups] = await Promise.all([
        productApi.hubspotUsers(),
        productApi.reviewerGroups(),
      ]);
      setUsers(nextUsers);
      setGroups(nextGroups);

      const knownUsers = new Set(nextUsers.map((user) => user.ownerId));
      setSelectedUsers(
        new Set(
          products
            .flatMap((product) => product.reviewers.map((reviewer) => reviewer.ownerId))
            .filter((ownerId) => knownUsers.has(ownerId)),
        ),
      );
      const knownGroups = new Set(nextGroups.map((group) => group.id));
      setSelectedGroups(
        new Set(
          products
            .flatMap((product) => (product.reviewGroups ?? []).map((group) => group.id))
            .filter((groupId) => knownGroups.has(groupId)),
        ),
      );
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [products]);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    void load();
  }, [load, open]);

  const counts = useMemo(() => {
    const result = new Map<string, number>();
    for (const product of products) {
      for (const reviewer of product.reviewers) {
        result.set(reviewer.ownerId, (result.get(reviewer.ownerId) ?? 0) + 1);
      }
    }
    return result;
  }, [products]);

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('nl');
    if (!query) return users;
    return users.filter((user) =>
      `${user.name} ${user.email}`.toLocaleLowerCase('nl').includes(query),
    );
  }, [search, users]);

  const save = async () => {
    setSaving(true);
    try {
      const result = await productApi.setReviewers(
        products.map((product) => product.id),
        { reviewerIds: [...selectedUsers], groupIds: [...selectedGroups] },
      );
      onSaved({ reviewers: result.reviewers, groups: result.groups });
      onOpenChange(false);
    } catch (error: unknown) {
      onError('Reviewers konden niet worden opgeslagen', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="grid h-[min(760px,calc(100vh-2rem))] !w-[calc(100vw-2rem)] !max-w-[680px] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0">
        <DialogHeader className="min-w-0 border-b pb-4 pl-5 pr-12 pt-5 text-left sm:pb-5 sm:pl-6 sm:pr-12 sm:pt-6">
          <DialogTitle className="text-xl">Productreviewers instellen</DialogTitle>
          <DialogDescription className="max-w-[68ch] leading-5">
            Kies wie offertes moet beoordelen waarin {products.length === 1
              ? 'het geselecteerde product staat'
              : `één of meer van de ${products.length} geselecteerde producten staan`}. Een
            verplichte reviewer geeft altijd zelf akkoord; uit een keuzegroep beoordeelt één
            aangewezen persoon per offerte.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 min-w-0 flex-col gap-3 px-5 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="font-semibold text-foreground">
              {products.length} {products.length === 1 ? 'product' : 'producten'} geselecteerd
            </span>
            <span className="shrink-0 text-muted-foreground" aria-live="polite">
              {selectedUsers.size} verplicht · {selectedGroups.size}{' '}
              {selectedGroups.size === 1 ? 'keuzegroep' : 'keuzegroepen'}
            </span>
          </div>

          {loading ? (
            <div className="flex min-h-40 flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              HubSpot-gebruikers laden…
            </div>
          ) : loadError ? (
            <div className="flex min-h-40 flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-muted-foreground">
                De gebruikerslijst kon niet uit HubSpot worden geladen.
              </p>
              <Button variant="outline" size="sm" onClick={() => void load()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Opnieuw proberen
              </Button>
            </div>
          ) : (
            <>
              <div className="min-w-0 shrink-0 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Keuzegroepen
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 shrink-0 text-xs"
                    onClick={onManageGroups}
                    disabled={saving}
                  >
                    Groepen beheren
                  </Button>
                </div>
                {groups.length === 0 ? (
                  <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
                    Nog geen keuzegroepen. Maak ze aan via Groepen beheren; ze gelden daarna voor
                    elk product dat je eraan koppelt.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {groups.map((group) => {
                      const checked = selectedGroups.has(group.id);
                      return (
                        <label
                          key={group.id}
                          className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${checked ? 'border-[#006B7C] bg-[#EAF7FA] text-[#00525F]' : 'hover:bg-muted/60'}`}
                          title={
                            group.members.map((member) => member.name).join(', ') || 'Nog geen leden'
                          }
                        >
                          <Checkbox
                            checked={checked}
                            disabled={saving}
                            onCheckedChange={() =>
                              setSelectedGroups((current) => {
                                const next = new Set(current);
                                if (next.has(group.id)) next.delete(group.id);
                                else next.add(group.id);
                                return next;
                              })
                            }
                          />
                          <span className="font-medium">{group.name}</span>
                          <span className="text-muted-foreground">
                            1 van {group.members.length}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Altijd akkoord nodig
              </span>

              <div className="relative shrink-0">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Zoek op naam of e-mailadres"
                  aria-label="Zoek HubSpot-gebruikers"
                  className="h-10 bg-background pl-9"
                  disabled={saving}
                />
              </div>

              <div
                className="scrollbar-thin min-h-40 min-w-0 flex-1 overflow-y-auto rounded-md border bg-background"
                role="group"
                aria-label="HubSpot-reviewers"
              >
                {visibleUsers.length === 0 ? (
                  <div className="flex min-h-40 flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
                    <Users className="h-5 w-5" />
                    {users.length
                      ? 'Geen gebruikers gevonden.'
                      : 'Geen actieve HubSpot-gebruikers met e-mailadres gevonden.'}
                  </div>
                ) : (
                  visibleUsers.map((user) => {
                    const checked = selectedUsers.has(user.ownerId);
                    const assignedCount = counts.get(user.ownerId) ?? 0;
                    return (
                      <label
                        key={user.ownerId}
                        className={`flex min-h-14 min-w-0 cursor-pointer items-center gap-3 border-b px-3 py-2.5 transition-colors last:border-b-0 hover:bg-muted/60 ${checked ? 'bg-[#EAF7FA]' : ''}`}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={saving}
                          onCheckedChange={() =>
                            setSelectedUsers((current) => {
                              const next = new Set(current);
                              if (next.has(user.ownerId)) next.delete(user.ownerId);
                              else next.add(user.ownerId);
                              return next;
                            })
                          }
                        />
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E0F3FA] text-xs font-semibold text-[#006B7C]">
                          {initials(user.name)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{user.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {user.email}
                          </span>
                        </span>
                        {assignedCount > 0 && assignedCount < products.length && (
                          <span
                            className="shrink-0 rounded-full bg-secondary px-2 py-1 text-[11px] font-medium text-secondary-foreground"
                            title={`Nu gekoppeld aan ${assignedCount} van de ${products.length} geselecteerde producten`}
                          >
                            {assignedCount}/{products.length}
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>

              <p className="text-xs leading-5 text-muted-foreground">
                De opgeslagen selectie geldt voor alle gekozen producten. Zonder reviewers en zonder
                keuzegroep vervalt de reviewverplichting.
              </p>
            </>
          )}
        </div>

        <DialogFooter className="min-w-0 gap-2 border-t bg-muted/30 px-5 py-4 sm:px-6 sm:space-x-0">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Annuleren
          </Button>
          <Button
            className="w-full sm:w-auto"
            onClick={() => void save()}
            disabled={loading || loadError || saving}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Reviewers opslaan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
